// =====================================================
// PRICING RULES REPOSITORY
// Stage 2: Participants & Pricing
// Repository pattern for pricing rules operations
// =====================================================

import * as pricingRulesService from '@/Services/pricingRules.service';
import type {
  PricingRule,
  CreatePricingRuleData,
  UpdatePricingRuleData,
} from '@/types/pricing.types';

// ===================
// ERROR WRAPPER
// ===================

/**
 * Wraps service calls with error handling
 */
async function withErrorHandling<T>(operation: () => Promise<T>): Promise<T> {
  try {
    return await operation();
  } catch (error) {
    console.error('Pricing Rules Repository Error:', error);
    throw error;
  }
}

// ===================
// REPOSITORY FUNCTIONS
// ===================

export const pricingRulesRepository = {
  /**
   * Fetch pricing rules for an event
   */
  async getPricingRulesByEvent(
    eventId: string,
    filters?: { is_active?: boolean; search?: string }
  ): Promise<PricingRule[]> {
    return withErrorHandling(() => pricingRulesService.getPricingRulesByEvent(eventId, filters));
  },

  /**
   * Fetch a single pricing rule
   */
  async getPricingRuleById(id: string): Promise<PricingRule | null> {
    return withErrorHandling(() => pricingRulesService.getPricingRuleById(id));
  },

  /**
   * Create a new pricing rule
   */
  async createPricingRule(data: CreatePricingRuleData): Promise<PricingRule> {
    return withErrorHandling(() => pricingRulesService.createPricingRule(data));
  },

  /**
   * Update a pricing rule
   */
  async updatePricingRule(id: string, data: UpdatePricingRuleData): Promise<PricingRule> {
    return withErrorHandling(() => pricingRulesService.updatePricingRule(id, data));
  },

  /**
   * Delete a pricing rule
   */
  async deletePricingRule(id: string): Promise<void> {
    return withErrorHandling(() => pricingRulesService.deletePricingRule(id));
  },

  /**
   * Toggle rule active status
   */
  async togglePricingRuleStatus(id: string, isActive: boolean): Promise<PricingRule> {
    return withErrorHandling(() => pricingRulesService.togglePricingRuleStatus(id, isActive));
  },

  /**
   * Reorder pricing rules
   */
  async reorderPricingRules(eventId: string, ruleIdsInOrder: string[]): Promise<void> {
    return withErrorHandling(() =>
      pricingRulesService.reorderPricingRules(eventId, ruleIdsInOrder)
    );
  },

  /**
   * Move rule up in priority
   */
  async movePricingRuleUp(eventId: string, ruleId: string): Promise<void> {
    return withErrorHandling(() => pricingRulesService.movePricingRuleUp(eventId, ruleId));
  },

  /**
   * Move rule down in priority
   */
  async movePricingRuleDown(eventId: string, ruleId: string): Promise<void> {
    return withErrorHandling(() => pricingRulesService.movePricingRuleDown(eventId, ruleId));
  },

  /**
   * Duplicate a pricing rule
   */
  async duplicatePricingRule(id: string): Promise<PricingRule> {
    return withErrorHandling(() => pricingRulesService.duplicatePricingRule(id));
  },

  /**
   * Copy rules to another event
   */
  async copyPricingRulesToEvent(
    sourceEventId: string,
    targetEventId: string
  ): Promise<PricingRule[]> {
    return withErrorHandling(() =>
      pricingRulesService.copyPricingRulesToEvent(sourceEventId, targetEventId)
    );
  },

  /**
   * Get pricing rule statistics
   */
  async getPricingRuleStatistics(eventId: string) {
    return withErrorHandling(() => pricingRulesService.getPricingRuleStatistics(eventId));
  },
};
